// src/store/useScenarioStore.ts

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { DamageResult, Field, Weather } from '../core/types';
import { calculateStats } from '../core/stats';
import { calculateDamage } from '../core/damage';
import { SPIRIT_MAP } from '../data/mock-spirits';
import { SKILL_MAP } from '../data/mock-skills';
import { NATURE_MAP } from '../data/mock-natures';
import { useTeamStore } from './useTeamStore';
export type SkillResultItem = {
  skillId: string;
  skillName: string;
  result: DamageResult;
};

interface ScenarioState {
  attackerIndex: number;
  defenderIndex: number;
  selectedSkillId: string | null;
  field: Field;
  result: DamageResult | null;

  skillResults: SkillResultItem[]

  setAttacker: (index: number) => void;
  setDefender: (index: number) => void;
  swapSides: () => void;
  setSkill: (skillId: string | null) => void;
  setWeather: (weather: Weather) => void;
  setCrit: (crit: boolean) => void;
  calculate: () => void;
  clearResult: () => void;

  // ✅新增这两个
  addSkillResult: (item: SkillResultItem) => void;
  clearSkillResults: () => void;
}

export const useScenarioStore = create<ScenarioState>()(
  persist(
    (set, get) => ({
      attackerIndex: 0,
      defenderIndex: 1,
      selectedSkillId: null,
      field: { weather: 'none', crit: false },
      result: null,

      skillResults: [], // 初始为空数组

      setAttacker: (index) => {
        set({ attackerIndex: index, selectedSkillId: null });
        get().calculate();
      },

      setDefender: (index) => {
        set({ defenderIndex: index });
        get().calculate();
      },

      swapSides: () => {
        const { attackerIndex, defenderIndex } = get();
        set({
          attackerIndex: defenderIndex,
          defenderIndex: attackerIndex,
          selectedSkillId: null,
        });
        get().calculate();
      },

      // ✅新增下面两个action
      addSkillResult: (item) => {
        const prev = get().skillResults;
        const filtered = prev.filter((x) => x.skillId !== item.skillId);
        set({ skillResults: [...filtered, item] });
      },
      clearSkillResults: () => set({ skillResults: [] }),

      setSkill: (skillId) => {
        set({ selectedSkillId: skillId });
        get().calculate();
      },

      setWeather: (weather) => {
        set((s) => ({ field: { ...s.field, weather } }));
        get().calculate();
      },

      setCrit: (crit) => {
        set((s) => ({ field: { ...s.field, crit } }));
        get().calculate();
      },

      calculate: () => {
        const { attackerIndex, defenderIndex, selectedSkillId, field } = get();
        const { team } = useTeamStore.getState();

        const attackerSlot = team.slots[attackerIndex];
        const defenderSlot = team.slots[defenderIndex];

        if (
          !attackerSlot?.spiritId ||
          !defenderSlot?.spiritId ||
          !selectedSkillId
        ) {
          set({ result: null });
          return;
        }

        const attackerSpirit = SPIRIT_MAP[attackerSlot.spiritId];
        const defenderSpirit = SPIRIT_MAP[defenderSlot.spiritId];
        const skill = SKILL_MAP[selectedSkillId];

        if (!attackerSpirit || !defenderSpirit || !skill) {
          set({ result: null });
          return;
        }
        const attackerBase = attackerSlot.customBaseStats ?? attackerSpirit.baseStats;
        const defenderBase = defenderSlot.customBaseStats ?? defenderSpirit.baseStats;
        
        const attackerStats = calculateStats(
  attackerBase,
  attackerSlot.ivs,
  attackerSlot.evs,
  NATURE_MAP[attackerSlot.nature],
  attackerSlot.level,
);

const defenderStats = calculateStats(
  defenderBase,
  defenderSlot.ivs,
  defenderSlot.evs,
  NATURE_MAP[defenderSlot.nature],
  defenderSlot.level,
);

        const result = calculateDamage({
          attacker: {
            spirit: attackerSpirit,
            stats: attackerStats,
            skill,
            boosts: attackerSlot.boosts,
          },
          defender: {
            spirit: defenderSpirit,
            stats: defenderStats,
            boosts: defenderSlot.boosts,
          },
          field,
          level: attackerSlot.level,
        });

        set({ result });
      },

      clearResult: () => set({ result: null }),
    }),
    {
      name: 'rococalc-scenario',
      version: 1,
      // result 不持久化，每次打开重新计算
      partialize: (state) => ({
        attackerIndex: state.attackerIndex,
        defenderIndex: state.defenderIndex,
        selectedSkillId: state.selectedSkillId,
        field: state.field,
      }),
    },
  ),
);
// ============================================================
// 🔄 自动重算：监听队伍变化，实时更新伤害结果
// ============================================================
useTeamStore.subscribe((state, prevState) => {
  // 只有当队伍数据真正发生变化时，才触发重算
  if (state.team !== prevState.team) {
    useScenarioStore.getState().calculate();
  }
});